/**
 * Runs tasks enqueued under the same key strictly one after another, never
 * overlapping — the mechanism that closes Story 2.3's self-conflict race
 * (review finding 3, VC-2.3-11's own guarantee under real network latency).
 *
 * The bug this exists to prevent: two sequential inline edits to the *same*
 * row (e.g. `display_name` then `role`, committed by `Tab` then `Enter`)
 * both read `expected_updated_at` from data available when each was typed.
 * If the second edit's write reaches the server before the first edit's
 * response has been processed, the second is genuinely built from a value
 * the first edit's own (not-yet-landed) success is about to make stale —
 * producing a `409` that blames a phantom third party for a change the same
 * session made a moment earlier. Invisible on a fast localhost round trip
 * (under ~30ms), real on any slower connection.
 *
 * Serialising by key alone does not fix staleness — the task itself still
 * has to read whatever is freshest *at the time it actually runs*, not at
 * the time it was enqueued (see `freshestKnownValue` in `inline-edit.ts`,
 * updated by the previous task in the same chain before it resolves). This
 * module only guarantees *when* each task runs, never overlapping a prior
 * task under the same key; it carries no opinion about what the task does.
 *
 * Tasks under different keys are never serialised against each other — two
 * edits to two different rows still run concurrently, exactly as before.
 */
export function createSerialQueue<Key>() {
  const chains = new Map<Key, Promise<void>>();

  return function enqueue<T>(key: Key, task: () => Promise<T>): Promise<T> {
    const previous = chains.get(key) ?? Promise.resolve();
    // `.catch()` on the awaited chain, not on the caller's own promise: one
    // task rejecting must never leave every later task under the same key
    // permanently blocked, and must never swallow *this* task's own
    // rejection from whoever called `enqueue`.
    const settled = previous.catch(() => undefined).then(task);
    chains.set(
      key,
      settled.then(
        () => undefined,
        () => undefined,
      ),
    );
    return settled;
  };
}
